import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  LinearProgress,
  Pagination,
  Stack,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Create authenticated axios instance
const authenticatedAxios = (() => {
  const api = axios.create({
    baseURL: '/api',
    headers: { 'Content-Type': 'application/json' }
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  return api;
})();

// API functions using authenticated axios
const api = {
  getCourses: (searchTerm) => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    return authenticatedAxios.get('/courses', { params }).then(res => res.data);
  },
  getCourse: (id) => authenticatedAxios.get(`/courses/${id}`).then(res => res.data),
  createCourse: (data) => authenticatedAxios.post('/courses', data).then(res => res.data),
  updateCourse: (id, data) => authenticatedAxios.put(`/courses/${id}`, data).then(res => res.data),
  deleteCourse: (id) => authenticatedAxios.delete(`/courses/${id}`).then(res => res.data),
};

// Course Form Dialog
const CourseFormDialog = ({ open, onClose, course, onSave }) => {
  const [formData, setFormData] = useState({
    short_name: '',
    full_name: '',
    description: '',
    is_active: true,
    ...course
  });

  React.useEffect(() => {
    if (course) {
      setFormData({
        short_name: course.short_name || '',
        full_name: course.full_name || '',
        description: course.description || '',
        is_active: course.is_active !== undefined ? course.is_active : true
      });
    } else {
      setFormData({
        short_name: '',
        full_name: '',
        description: '',
        is_active: true
      });
    }
  }, [course]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {course ? 'Edit Course' : 'Add New Course'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Short Name"
                value={formData.short_name}
                onChange={(e) => setFormData(prev => ({ ...prev, short_name: e.target.value }))}
                required
                helperText="Course code or abbreviation"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                required
                helperText="Complete course title"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={4}
                helperText="Course description and details"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active Course"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {course ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Course Details Dialog - Shows groups with [moodle_id] Group Name format  
const CourseDetailsDialog = ({ open, onClose, course }) => {
  const [courseGroups, setCourseGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (course && open) {
      setLoading(true);
      // Fetch groups for this course
      authenticatedAxios.get(`/groups?course_id=${course.moodle_id}`)
        .then(res => {
          const groups = res.data.success ? res.data.data : (res.data || []);
          setCourseGroups(Array.isArray(groups) ? groups : []);
        })
        .catch(error => {
          console.error('Error fetching course groups:', error);
          setCourseGroups([]);
        })
        .finally(() => setLoading(false));
    }
  }, [course, open]);

  if (!course) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Course Groups: {course.fullname}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Short Name</Typography>
            <Typography variant="body1" mb={2}>{course.shortname}</Typography>
             
            <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
            <Typography variant="body1" mb={2}>{course.fullname}</Typography>
            
            <Typography variant="subtitle2" color="text.secondary">Moodle ID</Typography>
            <Typography variant="body1" mb={2}>{course.moodle_id || course.id || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Chip 
              label={course.is_active !== false ? 'Active' : 'Inactive'} 
              color={course.is_active !== false ? 'success' : 'default'}
              size="small"
              sx={{ mb: 2 }}
            />
            
            <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'block' }}>Sync Status</Typography>
            <Chip 
              label={course.sync_status || 'never'} 
              color={course.sync_status === 'success' ? 'success' : course.sync_status === 'syncing' ? 'warning' : 'default'}
              size="small"
              sx={{ mb: 2 }}
            />
            
            <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'block' }}>Last Synced</Typography>
            <Typography variant="body1" mb={2}>
              {course.last_synced_at ? format(new Date(course.last_synced_at), 'MMM dd, yyyy HH:mm') : 'Never'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" mb={2}>
              Course Groups ({courseGroups.length})
            </Typography>
            {loading ? (
              <LinearProgress sx={{ mb: 2 }} />
            ) : courseGroups.length > 0 ? (
              <Stack spacing={1}>
                {courseGroups.map((group) => (
                  <Paper key={group.id} elevation={1} sx={{ p: 2 }}>
                    <Typography variant="body2">
                      [{group.moodle_group_id || group.moodle_id}] {group.group_name || group.name}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No groups found for this course
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">Description</Typography>
            <Typography variant="body1">{course.description || course.summary || 'No description available'}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Courses Component
const Courses = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pageSize] = useState(40);

  // Queries
  const { data: coursesData, isLoading, error } = useQuery(
    ['courses', searchTerm],
    () => api.getCourses(searchTerm),
    {
      // No automatic polling - refresh only on user actions or manual refresh
    }
  );


  // Mutations
  const createCourseMutation = useMutation(api.createCourse, {
    onSuccess: () => {
      toast.success('Course created successfully');
      queryClient.invalidateQueries('courses');
      setFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create course: ${error.response?.data?.message || error.message}`);
    }
  });

  const updateCourseMutation = useMutation(
    ({ id, data }) => api.updateCourse(id, data),
    {
      onSuccess: () => {
        toast.success('Course updated successfully');
        queryClient.invalidateQueries('courses');
        setFormOpen(false);
      },
      onError: (error) => {
        toast.error(`Failed to update course: ${error.response?.data?.message || error.message}`);
      }
    }
  );

  const deleteCourseMutation = useMutation(api.deleteCourse, {
    onSuccess: () => {
      toast.success('Course deleted successfully');
      queryClient.invalidateQueries('courses');
    },
    onError: (error) => {
      toast.error(`Failed to delete course: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleSave = (formData) => {
    if (selectedCourse) {
      updateCourseMutation.mutate({ id: selectedCourse.id, data: formData });
    } else {
      createCourseMutation.mutate(formData);
    }
  };

  const handleEdit = (course) => {
    setSelectedCourse(course);
    setFormOpen(true);
  };

  const handleView = (course) => {
    setSelectedCourse(course);
    setDetailsOpen(true);
  };

  const handleDelete = (course) => {
    if (window.confirm(`Are you sure you want to delete "${course.fullname || course.full_name}"?`)) {
      deleteCourseMutation.mutate(course.id);
    }
  };

  // Extract courses array from API response structure
  const courses = coursesData?.data || coursesData || [];
  const totalPages = Math.ceil((Array.isArray(courses) ? courses.length : 0) / pageSize);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load courses: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
          Course Management
        </Typography>
        <Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedCourse(null);
                setFormOpen(true);
              }}
              sx={{ mr: 1 }}
            >
              Add Course
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries('courses')}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>


      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Search by Course Name AND Short Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Courses Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course Name</TableCell>
                  <TableCell>Moodle ID</TableCell>
                  <TableCell>Groups (count)</TableCell>
                  <TableCell>Sync Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courses.map((course) => (
                  <TableRow key={course.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{course.fullname}</Typography>
                      <Chip label={course.shortname} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                      {course.summary && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {course.summary.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {course.moodle_id || course.id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <GroupIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {course.groups_count || 0} groups
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={course.sync_status || 'never'} 
                        color={course.sync_status === 'success' ? 'success' : course.sync_status === 'syncing' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Groups">
                        <IconButton size="small" onClick={() => handleView(course)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip title="Edit Course">
                            <IconButton size="small" onClick={() => handleEdit(course)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Course">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDelete(course)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CourseFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        course={selectedCourse}
        onSave={handleSave}
      />

      <CourseDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        course={selectedCourse}
      />
    </Box>
  );
};

export default Courses;
